import { Box, Flex, Heading, HStack, Spacer, Text, useDisclosure } from '@chakra-ui/react';
import { faAdd, faClock, faNoteSticky, faUser } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import { getFormattedServerDate } from '../../util/helpers/dateHelpers';
import { EntityNoteTypeBadge } from './types/entityNoteTypeBadge';
import { EntityNoteQueryType } from '../../enums/common/EntityNoteQueryType';
import { useEntityNotesQuery } from './apis/useEntityNotesQuery';
import { EntityNotesControls } from './EntityNotesControls';
import { EntityNotesPopover } from './EntityNotesPopover.tsx';
import { EntityNotesFilters } from './EntityNotesFilters';
import { EntityNotesFilterModel } from './types/entityNotesFilterModel';
import { EntityNoteModel } from '../../globalModels/entityNotes/entityNoteModel';
import {
  BaseButton,
  BaseButtonText,
  BaseCard,
  BaseDialog,
  BaseEmptyState,
  BaseHelpText,
  BaseIcon,
  BaseSkeletonStack,
  BaseTag,
  BaseToolbarButton,
  BaseTooltip
} from '@cks-internal/react-base';
import { formatFollowUpDateTime } from '../activities/editActivity/toolbar/followUpModal/utils/followUpFormatters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { EntityNoteType } from '../../enums/common/EntityNoteType.ts';

type Props = {
  entityId?: number;
  queryType: EntityNoteQueryType;
  noteDisplayTypes?: EntityNoteType[];
  height?: string;
  isReadOnly?: boolean;
  isPinned?: boolean;
};

export const EntityNotesDisplay = (props: Props) => {
  const [filterState, setFilterState] = useState<EntityNotesFilterModel>({
    Search: '',
    Active: true,
    EntityNoteTypes: props.noteDisplayTypes && props.noteDisplayTypes.length > 0 ? props.noteDisplayTypes : undefined,
    EntityNoteQueryType: props.queryType,
    IsPinned: props.isPinned
  });
  const notesQuery = useEntityNotesQuery(filterState, props.entityId);
  const [notes, setNotes] = useState<EntityNoteModel[]>();
  const noteDisclosure = useDisclosure();
  const PinnedColor = (isPinned: boolean) => {
    return isPinned ? 'orange.200' : 'gray.100';
  };
  const codeRedNoteDisclosure = useDisclosure();

  const [currentViewingNote, setCurrentViewingNote] = useState<EntityNoteModel | null>(null);

  useEffect(() => {
    if (notesQuery.data) {
      setNotes([...notesQuery.data]); // Ensure a new reference is created
    }
  }, [notesQuery.data]);

  // This will show as readonly cards + click to view content as a full-width div.
  if (
    props.noteDisplayTypes != null &&
    props.noteDisplayTypes.length == 1 &&
    props.noteDisplayTypes?.includes(EntityNoteType.CodeRed)
  ) {
    // alert(notes?.length);
    if (notes && notes.length > 0) {
      return (
        <>
          <Heading size={'md'} marginLeft={'16px'} marginTop={'24px'}>
            Code Red Notes ({notes.length})
          </Heading>
          <HStack
            gap={'16px'}
            backgroundColor={'orange.500'}
            padding={'24px'}
            width={'100%'}
            overflow={'scroll'}
            borderRadius={'16px'}
            marginBottom={'24px'}>
            {notes.map((val, index) => (
              <BaseCard
                cursor={'pointer'}
                onClick={() => {
                  setCurrentViewingNote(val);
                  codeRedNoteDisclosure.onOpen();
                }}
                width={'33%'}
                minW={'33%'}
                key={index}
                padding={'0px'}
                height={'150px'}
                headerProps={{ padding: '12px' }}
                header={
                  <HStack gap={'0px'}>
                    <EntityNoteTypeBadge noteType={val.EntityNoteType} />
                  </HStack>
                }
                footerProps={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: PinnedColor(val.IsPinned)
                }}
                footer={
                  <HStack width={'100%'} paddingLeft={'4px'} paddingRight={'8px'}>
                    <HStack gap={'4px'}>
                      <BaseIcon color={'primary.500'} height={'12px'} icon={faUser} />
                      <Text fontSize={'xs'} fontWeight={'semibold'}>
                        {val.CreatedByName}
                      </Text>
                    </HStack>
                    <Spacer />
                    {val.CreatedOn && (
                      <HStack gap={'4px'}>
                        <BaseIcon color={'primary.500'} height={'12px'} icon={faClock} />
                        <Text fontSize={'xs'} fontWeight={'semibold'}>
                          {getFormattedServerDate(val.CreatedOn)}
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                }>
                <Text lineClamp={2}>{val.Note}</Text>
              </BaseCard>
            ))}
          </HStack>
          <BaseDialog
            closeOnEsc={false}
            hideCloseButton={false}
            disclosure={codeRedNoteDisclosure}
            header={
              currentViewingNote != null && currentViewingNote.CreatedOn
                ? currentViewingNote.CreatedByName + ' @ ' + getFormattedServerDate(currentViewingNote.CreatedOn)
                : 'Code Red Note'
            }
            size={'md'}
            footerRender={
              <Box width={'100%'} textAlign={'end'}>
                <BaseButton minW={'100px'} onClick={codeRedNoteDisclosure.onClose} buttonText={BaseButtonText.OK} />
              </Box>
            }>
            {currentViewingNote ? currentViewingNote.Note : ''}
          </BaseDialog>
        </>
      );
    } else {
      return <></>;
    }
  }

  // Hide entire component in read-only mode when no notes exist
  if (props.isReadOnly && (!notes || notes.length === 0)) {
    return <></>;
  }

  // Render standard list component + the filter form and "Create" button
  return (
    <Flex
      width='100%'
      height={props.height || '100%'}
      flexDirection='column'
      borderRadius='md'
      boxShadow='sm'
      bg='white'
      overflow='hidden'>
      {/* Fixed Header Section */}
      <Flex flexDirection='column' flexShrink={0} borderBottom='1px solid' borderColor='gray.100' p={4}>
        <Flex justifyContent={'space-between'}>
          <Flex alignItems={'center'} gap={'8px'}>
            <>Notes</>
            {!props.isReadOnly && (
              <BaseHelpText
                text={
                  'Code Red notes and Agent notes are visible to agents during activities. Only pinned Agent notes will be displayed in notes view to agents.'
                }
              />
            )}
          </Flex>
          {!props.isReadOnly && (
            <EntityNotesPopover disclosure={noteDisclosure} queryType={props.queryType} id={props.entityId}>
              <BaseToolbarButton
                icon={faAdd}
                toolTip='Create New Note'
                onClick={() => {
                  noteDisclosure.onOpen();
                }}>
                Create New Note
              </BaseToolbarButton>
            </EntityNotesPopover>
          )}
        </Flex>
        {!props.isReadOnly && (
          <Flex alignItems={'center'} width={'100%'} mt={3}>
            <EntityNotesFilters
              onFilterChange={setFilterState}
              filterState={filterState}
              onCreate={noteDisclosure.onOpen}
            />
          </Flex>
        )}
      </Flex>

      {/* Scrollable Content Section */}
      <Box overflowY='auto' flex={1} p={4}>
        {notes && notes.length == 0 && (
          <BaseEmptyState
            isLoading={notesQuery.isLoading}
            title='No notes to show'
            icon={<FontAwesomeIcon icon={faNoteSticky} />}></BaseEmptyState>
        )}
        {notes &&
          notes.map((val, index) =>
            notesQuery.isLoading ? (
              <BaseSkeletonStack key={index} />
            ) : (
              <BaseCard
                key={index}
                marginTop={'16px'}
                padding={'0px'}
                height={'fit-content'}
                backgroundColor={val.FromLead ? 'gray.50' : undefined}
                headerProps={{ padding: '12px' }}
                header={
                  <HStack gap={'0px'}>
                    <EntityNoteTypeBadge noteType={val.EntityNoteType} />
                    <Spacer />
                    {val.ExpiresOnDate && (
                      <HStack gap={'4px'} mr={3}>
                        <BaseIcon color={'orange.500'} height={'12px'} icon={faClock} />
                        <Text fontSize={'xs'} fontWeight={'semibold'} color={'orange.500'}>
                          Expires: {formatFollowUpDateTime(val.ExpiresOnDate, val.ExpiresOnTime || null)}
                        </Text>
                      </HStack>
                    )}
                    {val.FromLead && (
                      <BaseTooltip content={val.FromLeadCompanyName}>
                        <Box>
                          <BaseTag colorPalette={'yellow'} mr={2}>
                            From Lead
                          </BaseTag>
                        </Box>
                      </BaseTooltip>
                    )}
                    <EntityNotesControls
                      typeId={props.entityId}
                      model={val}
                      queryType={props.queryType}
                      onPinnedChange={() => {
                        setNotes([...notes]);
                      }}
                      isReadOnly={props.isReadOnly || val.FromLead}
                    />
                  </HStack>
                }
                footerProps={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: PinnedColor(val.IsPinned)
                }}
                footer={
                  <HStack width={'100%'} paddingLeft={'4px'} paddingRight={'8px'}>
                    <HStack gap={'4px'}>
                      <BaseIcon color={'primary.500'} height={'12px'} icon={faUser} />
                      <Text fontSize={'xs'} fontWeight={'semibold'}>
                        {val.CreatedByName}
                      </Text>
                    </HStack>
                    <Spacer />
                    {val.CreatedOn && (
                      <HStack gap={'4px'}>
                        <BaseIcon color={'primary.500'} height={'12px'} icon={faClock} />
                        <Text fontSize={'xs'} fontWeight={'semibold'}>
                          {getFormattedServerDate(val.CreatedOn)}
                        </Text>
                      </HStack>
                    )}
                  </HStack>
                }>
                <Text userSelect='text'>{val.Note}</Text>
              </BaseCard>
            )
          )}
      </Box>
    </Flex>
  );
};
